/**
 * ContractualChargesSection Component
 *
 * Separate component for managing contractual charges at the bottom of the category list.
 * This keeps the contractual charges functionality isolated from the main Swimlane component.
 */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  ContractualChargesList,
  AddContractualChargeModal,
  AddContractualChargeInline,
} from 'components/ContractualCharges';
import { useContractualCharges } from 'hooks/useContractualCharges';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import DeleteConfirmModal from 'components/DeleteConfirmModal';

const ContractualChargesSection = ({ budgetId }) => {
  const { hasEditAccess, refetchBudgetId } = useHomeScreenContext();
  const {
    contractualCharges,
    createCharge,
    updateCharge,
    deleteCharge,
    repositionCharges,
  } = useContractualCharges(budgetId, Boolean(budgetId));

  const [showModal, setShowModal] = useState(false);
  const [showAddInput, setShowAddInput] = useState(false);
  const [editingChargeId, setEditingChargeId] = useState(null);
  const [chargeToDelete, setChargeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleAddClick = useCallback(() => {
    setShowAddInput(true);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setShowAddInput(false);
  }, []);

  const handleInlineSave = useCallback(
    async payload => {
      try {
        await createCharge(payload);
        setShowAddInput(false);
        refetchBudgetId?.();
      } catch (error) {
        console.error('Failed to create contractual charge:', error);
      }
    },
    [createCharge, refetchBudgetId]
  );

  const handleEdit = useCallback(charge => {
    setEditingChargeId(charge.id);
  }, []);

  const handleDelete = useCallback(charge => {
    setChargeToDelete(charge);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (chargeToDelete?.id && !isDeleting) {
      setIsDeleting(true);
      try {
        await deleteCharge(chargeToDelete.id);
        if (editingChargeId === chargeToDelete.id) {
          setEditingChargeId(null);
        }
        setChargeToDelete(null);
        refetchBudgetId?.();
      } catch (error) {
        console.error('Failed to delete contractual charge:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [
    chargeToDelete,
    deleteCharge,
    editingChargeId,
    isDeleting,
    refetchBudgetId,
  ]);

  const handleCancelDelete = useCallback(() => {
    if (!isDeleting) {
      setChargeToDelete(null);
    }
  }, [isDeleting]);

  const handleInlineEditSave = useCallback(
    async payload => {
      try {
        if (payload.id) {
          await updateCharge(payload);
        } else {
          await createCharge(payload);
        }
        setEditingChargeId(null);
        refetchBudgetId?.();
      } catch (error) {
        console.error('Failed to save contractual charge:', error);
        // Error toast is handled in useContractualCharges hook
      }
    },
    [createCharge, updateCharge, refetchBudgetId]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingChargeId(null);
  }, []);

  const handleSave = useCallback(
    async payload => {
      try {
        if (payload.id) {
          await updateCharge(payload);
        } else {
          await createCharge(payload);
        }
        // Close modal first to prevent double submissions
        setShowModal(false);
        // Refetch budget to update totals (this will also trigger React Query to refetch charges)
        refetchBudgetId?.();
      } catch (error) {
        console.error('Failed to save contractual charge:', error);
        // Re-throw error so modal can handle it
        throw error;
      }
    },
    [createCharge, updateCharge, refetchBudgetId]
  );

  const handleDragEnd = useCallback(
    result => {
      if (!result.destination) return;

      const { source, destination } = result;
      if (source.index !== destination.index) {
        const reorderedCharges = Array.from(contractualCharges);
        const [removed] = reorderedCharges.splice(source.index, 1);
        reorderedCharges.splice(destination.index, 0, removed);
        repositionCharges(reorderedCharges);
      }
    },
    [contractualCharges, repositionCharges]
  );

  // Always render the component, even if empty (shows "Add Contractual Charge" button)
  return (
    <div className="contractual-charges-section">
      <ContractualChargesList
        contractualCharges={contractualCharges || []}
        onAddClick={handleAddClick}
        onEdit={handleEdit}
        onDragEnd={handleDragEnd}
        hasEditAccess={hasEditAccess}
        budgetId={budgetId}
        showAddInput={showAddInput}
        editingChargeId={editingChargeId}
        showAddButton={true}
        renderEditForm={charge => (
          <AddContractualChargeInline
            key={`${budgetId}:${charge.id}`}
            charge={charge}
            onSave={handleInlineEditSave}
            onCancel={handleCancelEdit}
            onDelete={handleDelete}
            budgetId={budgetId}
          />
        )}
      />
      {showAddInput && (
        <AddContractualChargeInline
          key={`new-charge:${budgetId}`}
          onSave={handleInlineSave}
          onCancel={handleCancelAdd}
          budgetId={budgetId}
        />
      )}
      <AddContractualChargeModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        onSave={handleSave}
        budgetId={budgetId}
      />
      <DeleteConfirmModal
        showModal={Boolean(chargeToDelete)}
        handleOnClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Are you sure you want to delete this contractual charge?"
        itemName={chargeToDelete?.name || ''}
        confirmLabel="Delete"
        disabled={isDeleting}
      />
    </div>
  );
};

ContractualChargesSection.propTypes = {
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ContractualChargesSection.defaultProps = {
  budgetId: null,
};

export default ContractualChargesSection;
